import type { UploadFaceImageResultCode } from '@cobliteam/api-lib/src/dashboard/camera/definitions/APITypes';
import { useUploadDriverFaceImage } from '@cobliteam/hooks/src/useCamera/useDriverFaceCluster';
import { useTranslation } from '@cobliteam/i18n';
import type { FileMetadata as FileUploadMetadata } from '@cobliteam/ui/components/FileUpload';
import { useCallback, useEffect, useRef, useState } from 'react';

type FileMetadata = FileUploadMetadata & {
  faceImageUrl?: string;
};

interface UseImageUploadPoolHookProps {
  maxConcurrency?: number;
}

type UploadDoneHandler = (id: string, result: FileMetadata) => void;

type UploadErrorCopy = {
  key: string;
  icon: NonNullable<FileMetadata['error']>['icon'];
};

// The map holds i18n keys, not copy: these messages land on the preview overlay, so they must follow
// the language switcher instead of being pinned to pt-BR. Resolved through `t` at call time.
const resultCodeError: Record<Exclude<UploadFaceImageResultCode, 'OK'>, UploadErrorCopy> = {
  FACE_NOT_FOUND: {
    key: 'face_images.upload_errors.face_not_found',
    icon: 'frown',
  },
  FILE_TOO_LARGE: {
    key: 'face_images.upload_errors.file_too_large',
    icon: 'image-off',
  },
  LOW_QUALITY: {
    key: 'face_images.upload_errors.low_quality',
    icon: 'image-off',
  },
  NO_FILE: {
    key: 'face_images.upload_errors.no_file',
    icon: 'file-x-2',
  },
  OBSTRUCTED_IMAGE: {
    key: 'face_images.upload_errors.obstructed_image',
    icon: 'frown',
  },
  WRONG_CONTENT_TYPE: {
    key: 'face_images.upload_errors.wrong_content_type',
    icon: 'file-x-2',
  },
};

const GENERIC_UPLOAD_ERROR_KEY = 'face_images.upload_errors.generic';

const useFaceImageUploadPool = ({ maxConcurrency = 1 }: UseImageUploadPoolHookProps = {}) => {
  const { mutateAsync: uploadFaceImage } = useUploadDriverFaceImage();
  const { t } = useTranslation('drivers');

  const [hasTasks, setHasTasks] = useState(false);
  const queue = useRef<FileMetadata[]>([]);

  // The done handler lives in a ref, not in a render-scoped `let`. Consumers register it during
  // render (`handleDone(onUploadDone)`) and feed `uploadImages` into `useCallback` deps that end up
  // as `FileUpload`'s `onFilesChange` — so both returned functions must keep a stable identity.
  // A new `onFilesChange` on every render makes `useFileUploadCore`'s effect fire on every render,
  // which pushes the dropzone's internal file list back into the parent forever: an unbounded
  // render loop that also clobbers the finished upload, leaving the thumbnail spinning (BUG-2609).
  const uploadDoneHandler = useRef<UploadDoneHandler>(() => {});
  const activeCount = useRef(0);

  // Latest-value refs keep the callbacks below dependency-free (and therefore stable) without
  // capturing a stale mutation, concurrency limit or translator.
  const uploadFaceImageRef = useRef(uploadFaceImage);
  const maxConcurrencyRef = useRef(maxConcurrency);
  const tRef = useRef(t);
  useEffect(() => {
    uploadFaceImageRef.current = uploadFaceImage;
    maxConcurrencyRef.current = maxConcurrency;
    tRef.current = t;
  }, [uploadFaceImage, maxConcurrency, t]);

  const upload = useCallback(async (file: FileMetadata): Promise<FileMetadata> => {
    try {
      const faceImage = await uploadFaceImageRef.current({
        blobUrl: file.url,
        fileName: file.name,
      });

      const errorCopy = faceImage.code === 'OK' ? undefined : resultCodeError[faceImage.code];

      return {
        ...file,
        faceImageUrl: faceImage.face_image?.url,
        loading: false,
        error: errorCopy ? { message: tRef.current(errorCopy.key), icon: errorCopy.icon } : undefined,
      };
    } catch (_e: any) {
      // `loading: false` matters as much as the error itself — without it the preview keeps its
      // spinner forever on a failed upload.
      return {
        ...file,
        loading: false,
        error: { message: tRef.current(GENERIC_UPLOAD_ERROR_KEY) },
      };
    }
  }, []);

  // Fill every free slot instead of starting a single upload per call. Both consumers ask for
  // `maxConcurrency: 5`, but with one-shot dispatch the next file only left the queue from the
  // previous one's `finally` — turning the pool into a strictly serial queue.
  const processQueue = useCallback(() => {
    while (activeCount.current < maxConcurrencyRef.current && queue.current.length > 0) {
      const file = queue.current.shift();
      if (!file) break;

      activeCount.current += 1;

      // `upload` always settles on a later microtask, so `activeCount` can never be decremented
      // from inside this loop — no risk of spinning here.
      upload(file)
        .then((result) => {
          uploadDoneHandler.current(result.id, result);
        })
        .finally(() => {
          activeCount.current -= 1;
          processQueue();
        });
    }

    setHasTasks(queue.current.length > 0);
  }, [upload]);

  const uploadImages = useCallback(
    (files: FileMetadata[]) => {
      queue.current.push(...files);
      setHasTasks(true);
      processQueue();
    },
    [processQueue]
  );

  const handleDone = useCallback((handler: UploadDoneHandler) => {
    uploadDoneHandler.current = handler;
  }, []);

  return { uploadImages, hasTasks, handleDone };
};

export default useFaceImageUploadPool;
export type { FileMetadata };
